// Design System Border Tokens
// Consistent border styles and radius

// Border widths
export const borderWidths = {
  0: '0px',
  1: '1px',
  2: '2px',
  4: '4px',
  8: '8px',
};

// Border radius scale
export const borderRadius = {
  none: '0px',
  xs: '2px',
  sm: '4px',
  md: '6px',
  lg: '8px',
  xl: '12px',
  '2xl': '16px',
  '3xl': '24px',
  full: '9999px',
};

// Border styles
export const borderStyles = {
  solid: 'solid',
  dashed: 'dashed',
  dotted: 'dotted',
  none: 'none',
};

// Semantic border tokens
export const borders = {
  // Component borders
  component: {
    default: {
      width: borderWidths[1],
      style: borderStyles.solid,
      radius: borderRadius.md,
    },
    input: {
      width: borderWidths[1],
      style: borderStyles.solid,
      radius: borderRadius.md,
    },
    inputFocus: {
      width: borderWidths[2],
      style: borderStyles.solid,
      radius: borderRadius.md,
    },
    button: {
      width: borderWidths[1],
      style: borderStyles.solid,
      radius: borderRadius.md,
    },
    card: {
      width: borderWidths[1],
      style: borderStyles.solid,
      radius: borderRadius.lg,
    },
    modal: {
      width: borderWidths[0],
      style: borderStyles.none,
      radius: borderRadius.xl,
    },
    tooltip: {
      width: borderWidths[1],
      style: borderStyles.solid,
      radius: borderRadius.sm,
    },
  },
  
  // Layout borders
  layout: {
    divider: {
      width: borderWidths[1],
      style: borderStyles.solid,
    },
    sidebar: {
      width: borderWidths[1],
      style: borderStyles.solid,
    },
    header: {
      width: borderWidths[1],
      style: borderStyles.solid,
    },
  },
  
  // State borders
  state: {
    default: borderWidths[1],
    hover: borderWidths[1],
    focus: borderWidths[2],
    active: borderWidths[2],
    disabled: borderWidths[1],
    error: borderWidths[2],
    success: borderWidths[2],
    warning: borderWidths[2],
  },
};

// Border color tokens (theme-aware)
export const borderColors = {
  light: {
    default: '#E5E7EB',      // gray-200
    subtle: '#F3F4F6',       // gray-100
    strong: '#D1D5DB',       // gray-300
    interactive: '#6B7280',   // gray-500
    brand: '#2B5885',        // navy-600
    accent: '#f2c526',       // gold-500
    error: '#DC2626',        // red-600
    warning: '#D97706',      // orange-600
    success: '#059669',      // green-600
    info: '#2563EB',         // blue-600
  },
  dark: {
    default: 'rgba(255, 255, 255, 0.12)',
    subtle: 'rgba(255, 255, 255, 0.08)',
    strong: 'rgba(255, 255, 255, 0.16)',
    interactive: 'rgba(255, 255, 255, 0.24)',
    brand: '#4A7FC7',        // navy-500
    accent: '#f2c526',       // gold-500
    error: '#F87171',        // red-400
    warning: '#FBBF24',      // orange-400
    success: '#34D399',      // green-400
    info: '#60A5FA',         // blue-400
  },
};

// Border utilities
export const borderUtils = {
  // Get border string
  getBorder: (width = 1, style = 'solid', color = 'default', theme = 'light') => {
    const w = borderWidths[width] || borderWidths[1];
    const s = borderStyles[style] || borderStyles.solid;
    const c = borderColors[theme][color] || borderColors[theme].default;
    return `${w} ${s} ${c}`;
  },
  
  // Get component border
  getComponentBorder: (component = 'default', theme = 'light') => {
    const config = borders.component[component] || borders.component.default;
    const color = borderColors[theme].default;
    return `${config.width} ${config.style} ${color}`;
  },
  
  // Get border radius
  getRadius: (size = 'md') => {
    return borderRadius[size] || borderRadius.md;
  },
  
  // Get state border
  getStateBorder: (state = 'default', theme = 'light') => {
    const width = borders.state[state] || borders.state.default;
    const color = borderColors[theme][state] || borderColors[theme].default;
    return `${width} solid ${color}`;
  },
  
  // Create custom border
  createBorder: (width, style, color) => {
    return `${width} ${style} ${color}`;
  },
};

// Common border combinations
export const borderCombinations = {
  // Input borders
  input: {
    default: (theme) => borderUtils.getBorder(1, 'solid', 'default', theme),
    hover: (theme) => borderUtils.getBorder(1, 'solid', 'interactive', theme),
    focus: (theme) => borderUtils.getBorder(2, 'solid', 'brand', theme),
    error: (theme) => borderUtils.getBorder(2, 'solid', 'error', theme),
    success: (theme) => borderUtils.getBorder(2, 'solid', 'success', theme),
  },
  
  // Button borders
  button: {
    default: (theme) => borderUtils.getBorder(1, 'solid', 'default', theme),
    primary: (theme) => borderUtils.getBorder(1, 'solid', 'brand', theme),
    secondary: (theme) => borderUtils.getBorder(1, 'solid', 'accent', theme),
  },
  
  // Card borders
  card: {
    default: (theme) => borderUtils.getBorder(1, 'solid', 'subtle', theme),
    elevated: (theme) => borderUtils.getBorder(1, 'solid', 'default', theme),
    interactive: (theme) => borderUtils.getBorder(1, 'solid', 'interactive', theme),
  },
};

export default {
  widths: borderWidths,
  radius: borderRadius,
  styles: borderStyles,
  semantic: borders,
  colors: borderColors,
  utils: borderUtils,
  combinations: borderCombinations,
};